import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Flex,
  Heading,
  Text,
  IconButton,
  Tooltip,
  useColorModeValue,
  useMediaQuery,
  Badge
} from '@chakra-ui/react';
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { FaStar, FaInfoCircle } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getOptimizedHeroImage } from '../../../utils/imageOptimizer';
import MemoryManager from '../../../utils/MemoryManager';
import { buildVideoUrl } from '../../../utils/slugify';

// Large featured card — much bigger than regular VideoCards
const FeaturedCard = ({ video, showInfo = false }) => {
  const navigate = useNavigate();
  const [imageLoaded, setImageLoaded] = useState(false);

  const handleClick = () => {
    if (video?.id) {
      navigate(buildVideoUrl(video.id, video.title));
    }
  };

  const getThumbnailUrl = () => {
    if (video.thumbnail) {
      return video.thumbnail.startsWith('http')
        ? video.thumbnail
        : `https://bunny-burrow-pull-1.b-cdn.net/${video.thumbnail}`;
    }
    return 'https://bunny-burrow-pull-1.b-cdn.net/Default-thumbnail.png';
  };

  const rawThumbnailUrl = getThumbnailUrl();
  const thumbnailUrl = getOptimizedHeroImage(rawThumbnailUrl);

  // Preload the image and trigger fade-in when loaded
  useEffect(() => {
    if (!thumbnailUrl) return;
    const img = new Image();
    img.onload = () => setImageLoaded(true);
    img.onerror = () => setImageLoaded(true); // Show even on error
    img.src = thumbnailUrl;
  }, [thumbnailUrl]);

  return (
    <Box
      minW={{ base: '300px', md: '420px', lg: '480px' }}
      maxW={{ base: '300px', md: '420px', lg: '480px' }}
      h={{ base: '169px', md: '236px', lg: '270px' }}
      borderRadius="12px"
      overflow="hidden"
      position="relative"
      cursor="pointer"
      onClick={handleClick}
      transition="all 0.3s ease"
      _hover={{
        transform: 'scale(1.04)',
        boxShadow: '0 8px 30px rgba(0,0,0,0.4)',
      }}
      boxShadow="0 4px 15px rgba(0,0,0,0.3)"
      flexShrink={0}
    >
      {/* Thumbnail */}
      <Box
        bgImage={imageLoaded ? `url(${thumbnailUrl})` : 'none'}
        bgSize="cover"
        bgPosition="center"
        bgRepeat="no-repeat"
        backgroundColor="black"
        w="100%"
        h="100%"
        opacity={imageLoaded ? 1 : 0}
        transition="opacity 0.4s ease-in"
      />

      {/* Gradient overlay bottom */}
      <Box
        position="absolute"
        bottom="0"
        left="0"
        right="0"
        h="55%"
        bgGradient="linear(to-t, blackAlpha.900, blackAlpha.600, transparent)"
        pointerEvents="none"
        opacity={showInfo ? 1 : 0}
        transition="opacity 0.3s ease"
      />

      {/* Title + description overlay */}
      <Box
        position="absolute"
        bottom="0"
        w="100%"
        p={{ base: 3, md: 4 }}
        color="white"
        transform={showInfo ? 'translateY(0)' : 'translateY(100%)'}
        transition="transform 0.3s ease"
      >
        <Heading size={{ base: 'sm', md: 'md' }} noOfLines={1} textShadow="0 2px 4px rgba(0,0,0,0.5)">
          {video.title}
        </Heading>
        <Text
          fontSize={{ base: 'xs', md: 'sm' }}
          mt={1}
          noOfLines={2}
          opacity={0.9}
          textShadow="0 1px 3px rgba(0,0,0,0.5)"
        >
          {video.description || video.shortDescription || ''}
        </Text>
      </Box>


    </Box>
  );
};

// The main carousel component for featured/large cards
const FeaturedCarousel = ({ data = [], title = 'Burrow Picks' }) => {
  const [showControls, setShowControls] = useState(false);
  const [isInfoActive, setIsInfoActive] = useState(false);
  const [, forceUpdate] = useState(0); // trigger re-renders
  const carouselRef = useRef(null);
  const scrollPosRef = useRef(0); // track scroll in a ref for instant reads
  const [isMobile] = useMediaQuery('(max-width: 768px)');

  const bgColor = useColorModeValue('gray.50', 'gray.800');
  const arrowColor = useColorModeValue('gray.800', 'white');

  // --- Drag-to-scroll state ---
  const isDragging = useRef(false);
  const dragStartX = useRef(0);
  const dragScrollLeft = useRef(0);
  const hasDragged = useRef(false);

  // Update scroll position ref and trigger re-render
  const syncScrollPosition = useCallback(() => {
    if (!carouselRef.current) return;
    const pos = carouselRef.current.scrollLeft;
    if (Math.abs(pos - scrollPosRef.current) > 1) {
      scrollPosRef.current = pos;
      forceUpdate(n => n + 1);
    }
  }, []);

  const onMouseDown = useCallback((e) => {
    if (!carouselRef.current) return;
    e.preventDefault(); // Prevent native image/link drag
    isDragging.current = true;
    hasDragged.current = false;
    dragStartX.current = e.pageX - carouselRef.current.offsetLeft;
    dragScrollLeft.current = carouselRef.current.scrollLeft;
    carouselRef.current.style.cursor = 'grabbing';
    carouselRef.current.style.userSelect = 'none';
  }, []);

  // Block native drag on images/links inside the carousel
  const onDragStart = useCallback((e) => {
    e.preventDefault();
  }, []);

  const onMouseMove = useCallback((e) => {
    if (!isDragging.current || !carouselRef.current) return;
    e.preventDefault();
    const x = e.pageX - carouselRef.current.offsetLeft;
    const walk = (x - dragStartX.current) * 1.5;
    if (Math.abs(walk) > 5) hasDragged.current = true;
    carouselRef.current.scrollLeft = dragScrollLeft.current - walk;
    syncScrollPosition();
  }, [syncScrollPosition]);

  const onMouseUp = useCallback(() => {
    if (!carouselRef.current) return;
    isDragging.current = false;
    carouselRef.current.style.cursor = 'grab';
    carouselRef.current.style.userSelect = '';
    syncScrollPosition();
    if (hasDragged.current) {
      const preventClick = (e) => {
        e.stopPropagation();
        e.preventDefault();
      };
      carouselRef.current.addEventListener('click', preventClick, { capture: true, once: true });
    }
  }, [syncScrollPosition]);

  const onMouseLeave = useCallback(() => {
    if (isDragging.current) onMouseUp();
  }, [onMouseUp]);

  // Scroll by card width, then poll for position update
  const scrollRow = (direction) => {
    if (!carouselRef.current) return;
    const cardWidth = isMobile ? 316 : 500;
    const scrollAmount = direction === 'left' ? -cardWidth : cardWidth;
    carouselRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    // Poll position during smooth scroll since scroll event may not fire reliably - Use MemoryManager for cleanup
    const pollId = MemoryManager.setInterval(() => {
      syncScrollPosition();
    }, 50, 'FeaturedCarousel-scroll');
    MemoryManager.setTimeout(() => MemoryManager.clearInterval(pollId), 600, 'FeaturedCarousel-scroll');
  };

  // Listen for native scroll events
  useEffect(() => {
    const el = carouselRef.current;
    if (!el) return;
    const handler = () => syncScrollPosition();
    el.addEventListener('scroll', handler, { passive: true });
    // Initial sync
    syncScrollPosition();
    return () => el.removeEventListener('scroll', handler);
  }, [syncScrollPosition]);

  const canScrollLeft = () => scrollPosRef.current > 2;
  
  const canScrollRight = () => {
    if (!carouselRef.current) return false;
    const el = carouselRef.current;
    return el.scrollWidth > el.clientWidth &&
      scrollPosRef.current < el.scrollWidth - el.clientWidth - 10;
  };

  if (!data || data.length === 0) return null;

  return (
    <Box
      mb={{ base: 4, md: 8 }}
      py={{ base: 4, md: 6 }}
      px={{ base: 2, md: 4 }}
      bg="linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)"
      borderRadius={{ base: '0', md: '12px' }}
      mx={{ base: 0, md: 4 }}
      position="relative"
      overflow="hidden"
      boxShadow="0 4px 20px rgba(0,0,0,0.3)"
    >
      {/* Subtle pattern overlay */}
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        opacity={0.05}
        bgImage="radial-gradient(circle at 25% 25%, white 1px, transparent 1px)"
        bgSize="30px 30px"
        pointerEvents="none"
      />

      {/* Header */}
      <Flex align="center" ml={{ base: 2, md: 4 }} mb={{ base: 3, md: 5 }} position="relative" zIndex={1}>
        <Box mr={2} color="#d4a017">
          <FaStar size="1.4em" />
        </Box>
        <Heading
          size={{ base: 'md', md: 'lg' }}
          color="white"
          letterSpacing="wide"
        >
          {title}
        </Heading>
        <Text
          ml={3}
          fontSize={{ base: 'xs', md: 'sm' }}
          color="whiteAlpha.700"
          fontStyle="italic"
        >
          Our hand-picked favorites
        </Text>
        <Tooltip label="Toggle info display" fontSize="sm">
          <IconButton
            aria-label="Toggle information"
            icon={<FaInfoCircle />}
            size="sm"
            variant="ghost"
            ml={3}
            color={isInfoActive ? "yellow.400" : "whiteAlpha.500"}
            onClick={() => setIsInfoActive(!isInfoActive)}
            _hover={{ color: "yellow.300" }}
          />
        </Tooltip>
      </Flex>

      {/* Carousel */}
      <Box
        position="relative"
        onMouseEnter={() => setShowControls(true)}
        onMouseLeave={() => { setShowControls(false); }}
        zIndex={1}
      >
        {/* Scrollable row */}
        <Flex
          ref={carouselRef}
          overflowX="auto"
          overflowY="hidden"
          css={{
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
          gap={{ base: 3, md: 5 }}
          px={{ base: 2, md: 4 }}
          py={3}
          cursor="grab"
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
          onMouseLeave={onMouseLeave}
          onDragStart={onDragStart}
        >
          {data.map((video, index) => (
            <FeaturedCard key={video?.id || index} video={video} showInfo={showControls || isInfoActive} />
          ))}
        </Flex>

        {/* Left arrow — always visible on hover when scrolled */}
        <Box
          position="absolute"
          left={0}
          top={0}
          bottom={0}
          w="60px"
          display="flex"
          alignItems="center"
          justifyContent="center"
          bg="linear-gradient(to right, rgba(0,0,0,0.7), transparent)"
          borderRadius="12px 0 0 12px"
          opacity={canScrollLeft() ? 1 : 0}
          pointerEvents={canScrollLeft() ? 'auto' : 'none'}
          transition="opacity 0.3s"
          zIndex={10}
          cursor="pointer"
          onClick={(e) => { e.stopPropagation(); scrollRow('left'); }}
          _hover={{ bg: 'linear-gradient(to right, rgba(0,0,0,0.9), transparent)' }}
        >
          <ChevronLeftIcon boxSize={10} color="white" />
        </Box>

        {/* Right arrow — always visible when more content */}
        <Box
          position="absolute"
          right={0}
          top={0}
          bottom={0}
          w="60px"
          display="flex"
          alignItems="center"
          justifyContent="center"
          bg="linear-gradient(to left, rgba(0,0,0,0.7), transparent)"
          borderRadius="0 12px 12px 0"
          opacity={canScrollRight() ? 1 : 0}
          pointerEvents={canScrollRight() ? 'auto' : 'none'}
          transition="opacity 0.3s"
          zIndex={10}
          cursor="pointer"
          onClick={(e) => { e.stopPropagation(); scrollRow('right'); }}
          _hover={{ bg: 'linear-gradient(to left, rgba(0,0,0,0.9), transparent)' }}
        >
          <ChevronRightIcon boxSize={10} color="white" />
        </Box>
      </Box>
    </Box>
  );
};

export default FeaturedCarousel;
